import { useNavigate } from "@tanstack/react-router";
import { BOX_BAGLANTI, PIN_BAGLANTI, mmMetni, type KaynakModu } from "@oasis/domain";
import { useVeri } from "@/lib/veri";
import { Band, Bos, Etiket, ExcelButonu, Hata, Kutu, Sayfa, tabloBas, tabloHucre } from "@/components/parcalar";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";

/**
 * D-037: kaynak başına fit-up formu (P); kaynağın tipi ve box ucundan mesafesi (D-036).
 * D-070: `mesafeKayitYok` = mesafesi hâlâ boş ve kesim satırı "No distance record available" ile onaylanmış kaynak.
 */
type FitupFormu = { weldId: string; no: number; joint: string | null; mesafeMm: number | null; pos: number | null; hazir: boolean; release: boolean; wpsKod: string | null; mesafeKayitYok?: boolean };

interface Kalem {
  id: string; riserRn: string; grup: string; hat: string; poz: string | null; sn: string | null; is: string; rotaKod: string; deneme: number;
  adim: number; adimAd: string; adimSayisi: number; bitti: boolean; malzemeEksik: boolean;
  fitupAdimi: boolean; kaynakAdimi: boolean;
  /** D-037: P = kesim planı, kaynak başına form · L = eski iş, kalem düzeyi tek form · 0 = plan yok, kaynak yok. */
  mod: KaynakModu;
  /** Yalnız L: weld_id boş kalem düzeyi form. */
  fitup: { hazir: boolean; release: boolean; wpsKod: string | null } | null;
  /** Yalnız P: kaynak no sırasıyla. */
  fitupFormlari: FitupFormu[];
  sayac: { toplam: number; hazir: number; release: number; pasolu: number };
  weld: { agiz: number; paso: number; kapali: number };
  /** "No cut" ile onaylanmış plan (mod 0): satır "Cut plan not approved" yerine "No cut: no welds" yazar. */
  kesimYok: boolean;
}

/** Fit-up kuyruğunun satırı: P'de her kaynak ayrı satır; L'de tek "Item-level" satırı; mod 0'da fit-up adımındaki iş "Cut plan not approved". */
type Satir = { anahtar: string; k: Kalem; tur: "kaynak" | "kalem" | "plansiz"; form: FitupFormu | null; durum: { hazir: boolean; release: boolean; wpsKod: string | null } | null };

type Mod = "hepsi" | "fitup" | "weld";
const BASLIK: Record<Mod, { ust: string; ad: string; alt: string }> = {
  hepsi: { ust: "3.x · E-17…E-22", ad: cevir("Components"), alt: cevir("All components with Repair route opened. Click on row: component page (material chain, work chain, file shelf).") },
  fitup: { ust: "G1 · Duvar 11", ad: cevir("Fit-up queue"), alt: cevir("Components at Fit-up step. Foreman says \"We are ready\", quality controller fills form and gives release; welding does not open without release.") },
  weld: { ust: "QAF 038", ad: cevir("Weld map queue"), alt: cevir("Components with weld groove. Pass entry and sign-offs are on weld map screen; pass cannot be entered without G1 lock.") },
};

/** "630 mm" · referansı boş flanş kaynağında "at box flange weld" · eski kayıtta "24 %" · D-070: mesafe kaydı olmayan kaynakta "no distance record". */
export function konum(f: Pick<FitupFormu, "joint" | "mesafeMm" | "pos" | "mesafeKayitYok">): string {
  if (f.mesafeMm !== null) return `${mmMetni(f.mesafeMm)} mm`;
  if (f.pos !== null) return `${f.pos} %`;
  return f.joint === BOX_BAGLANTI ? cevir("at box flange weld") : f.joint === PIN_BAGLANTI ? cevir("at pin flange weld") : f.mesafeKayitYok ? cevir("no distance record") : "—";
}

/** Sol menüdeki Komponent / Fit-up / Weld map başlıkları: aynı kütüğün üç görünümü. */
export function KomponentListe({ mod }: { mod: Mod }) {
  const { veri, hata } = useVeri<Kalem[]>("/planlama/komponentler");
  const nav = useNavigate();
  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;

  const kalemler = veri.filter((k) => (mod === "hepsi" ? true : mod === "fitup" ? k.fitupFormlari.length > 0 || k.fitup !== null || k.fitupAdimi : k.weld.agiz > 0));
  const b = BASLIK[mod];
  const ac = (k: Kalem) => nav({ to: mod === "weld" ? "/weldmap/$id" : "/komponent/$id", params: { id: k.id } });

  // D-037: fit-up kuyruğunda her kaynak bir satır; eski iş tek kalem düzeyi satır; planı olmayan iş komponent sayfasına gider.
  const satirlar: Satir[] = kalemler.flatMap((k): Satir[] =>
    k.fitupFormlari.length
      ? k.fitupFormlari.map((f) => ({ anahtar: `${k.id}:${f.weldId}`, k, tur: "kaynak", form: f, durum: f }))
      : k.fitup || k.mod === "L"
        ? [{ anahtar: k.id, k, tur: "kalem", form: null, durum: k.fitup }]
        : [{ anahtar: k.id, k, tur: "plansiz", form: null, durum: null }],
  );
  const satirAc = (s: Satir) =>
    s.form ? nav({ to: "/fitup/$id/$weldId", params: { id: s.k.id, weldId: s.form.weldId } })
      : s.tur === "kalem" ? nav({ to: "/fitup/$id", params: { id: s.k.id } })
        : nav({ to: "/komponent/$id", params: { id: s.k.id } });
  const formSayisi = satirlar.filter((s) => s.tur !== "plansiz").length;

  const adimHucresi = (k: Kalem) => (k.adim === -1 ? <Etiket tone="bad">{cevir("waiting for material")}</Etiket> : k.bitti ? <Etiket tone="ok">{cevir("route completed")}</Etiket> : <><Etiket tone="info">{k.adimAd}</Etiket><span className="ml-1 text-xs text-ink-3 tnum">{k.adim + 1}/{k.adimSayisi}</span></>);
  const fitupDurumu = (d: Satir["durum"], malzemeEksik: boolean) =>
    d?.release ? <Etiket tone="ok">{cevir("G1 open")}{d.wpsKod ? ` · ${d.wpsKod}` : ""}</Etiket>
      : d?.hazir ? <Etiket tone="warn">{cevir("waiting for quality control")}</Etiket>
        : malzemeEksik ? <Etiket tone="bad">{cevir("material missing")}</Etiket> : <Etiket>—</Etiket>;

  return (
    <Sayfa ust={b.ust} baslik={b.ad} alt={b.alt}>
      <Kutu aksiyon={<span className="flex items-center gap-2">{mod === "fitup" ? cevir`${formSayisi} fit-up forms (${kalemler.length} component)` : <>{kalemler.length} {cevir("component")}</>} <ExcelButonu gorunum={mod === "weld" ? "weld" : "komponent"} /></span>} baslik={mod === "hepsi" ? cevir("Open and closed Items") : mod === "fitup" ? cevir("Waiting / opened grooves for Fit-up") : cevir("Components with weld groove")} padded={false}>
        <div className="scroll-thin overflow-x-auto">
          {mod === "fitup" ? (
            <table className="w-full min-w-[960px]">
              <thead>
                <tr className="border-b border-line">
                  {["Riser", cevir("Component"), "SN", cevir("Task"), "Weld", cevir("Route"), cevir("Active step"), cevir("Status")].map((x) => <th key={x} className={tabloBas}>{x}</th>)}
                </tr>
              </thead>
              <tbody>
                {satirlar.map((s) => {
                  const k = s.k;
                  return (
                    <tr key={s.anahtar} onClick={() => void satirAc(s)} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-2">
                      <td className={tabloHucre}><b className="font-mono">{k.riserRn}</b></td>
                      <td className={tabloHucre}>{k.hat}{k.poz ? ` · ${k.poz}` : ""}</td>
                      <td className={cn(tabloHucre, "font-mono text-ink-2")}>{k.sn ?? "—"}</td>
                      <td className={tabloHucre}>{k.is}</td>
                      <td className={tabloHucre}>
                        {s.form ? <><b>W{s.form.no}</b> · {s.form.joint ?? "—"} · {konum(s.form)}</>
                          : s.tur === "kalem" ? cevir("Item-level fit-up (covers all welds)")
                            : <Etiket tone="warn">{k.kesimYok ? cevir("No cut: no welds") : cevir("Cut plan not approved")}</Etiket>}
                      </td>
                      <td className={tabloHucre}>{k.rotaKod}{k.deneme > 0 && <Etiket tone="warn" className="ml-1">{cevir("trial")} {k.deneme + 1}</Etiket>}</td>
                      <td className={tabloHucre}>
                        {adimHucresi(k)}
                        {k.mod === "P" && k.sayac.toplam > 0 && <div className="mt-0.5 text-xs text-ink-3 tnum">{cevir`G1 · ${k.sayac.release}/${k.sayac.toplam} welds released`}</div>}
                      </td>
                      <td className={tabloHucre}>{fitupDurumu(s.durum, k.malzemeEksik)}</td>
                    </tr>
                  );
                })}
                {!satirlar.length && <tr><td colSpan={8}><Bos>{cevir("No component in this view.")}</Bos></td></tr>}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="border-b border-line">
                  {["Riser", cevir("Component"), "SN", cevir("Task"), cevir("Route"), cevir("Active step"), mod === "weld" ? cevir("Groove / pass") : cevir("Status")].map((x) => <th key={x} className={tabloBas}>{x}</th>)}
                </tr>
              </thead>
              <tbody>
                {kalemler.map((k) => (
                  <tr key={k.id} onClick={() => void ac(k)} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-2">
                    <td className={tabloHucre}><b className="font-mono">{k.riserRn}</b></td>
                    <td className={tabloHucre}>{k.hat}{k.poz ? ` · ${k.poz}` : ""}</td>
                    <td className={cn(tabloHucre, "font-mono text-ink-2")}>{k.sn ?? "—"}</td>
                    <td className={tabloHucre}>{k.is}</td>
                    <td className={tabloHucre}>{k.rotaKod}{k.deneme > 0 && <Etiket tone="warn" className="ml-1">{cevir("trial")} {k.deneme + 1}</Etiket>}</td>
                    <td className={tabloHucre}>{adimHucresi(k)}</td>
                    <td className={tabloHucre}>
                      {mod === "weld"
                        ? <span className="tnum">{k.weld.agiz} {cevir("groove ·")} {k.weld.paso} {cevir("pass")}{k.weld.kapali ? ` · ${k.weld.kapali} closed` : ""}</span>
                        : k.mod === "P" && k.sayac.toplam > 0
                          ? <Etiket tone={k.sayac.release === k.sayac.toplam ? "ok" : k.sayac.hazir > k.sayac.release ? "warn" : "neutral"}>{cevir`G1 · ${k.sayac.release}/${k.sayac.toplam} welds released`}</Etiket>
                          : fitupDurumu(k.fitup, k.malzemeEksik)}
                    </td>
                  </tr>
                ))}
                {!kalemler.length && <tr><td colSpan={7}><Bos>{cevir("No component in this view.")}</Bos></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </Kutu>
      <Band tip="bilgi">
        {mod === "fitup"
          ? cevir("Separates the foreman preparing Fit-up form from the quality controller approving it (Duvar 11); measurements are checked against WPS datasheet range.")
          : mod === "weld"
            ? cevir("Welds come from the approved cut plan: one weld and one fit-up form per approved cut. Pass entry follows WPS process flow (first minimum TIG pass).")
            : cevir("When component is approved in Inspection verification, it opens with route steps; work chain does not start before material chain is complete.")}
      </Band>
    </Sayfa>
  );
}
